<template>
  <div>
    <div v-show="false" ref="pdfTemplate" class="po-report-pdf-template">
      <div class="po-report-pdf-page">
        <header class="po-report-pdf-page-header">
          <div class="po-report-pdf-header">
            <div class="po-report-pdf-logo-wrap">
              <img :src="logoUrl" alt="Dimitra Logo" class="po-report-pdf-logo" />
            </div>
            <div class="po-report-pdf-title-banner">
              <h2>{{ reportTitle }}</h2>
            </div>
          </div>
        </header>

        <div class="po-report-pdf-content">
        <section class="po-report-pdf-section po-report-pdf-section--shaded">
          <h3 class="po-report-pdf-section-title">{{ lotInformationTitle }}</h3>
          <div class="po-report-pdf-lot-panel">
            <div class="po-report-pdf-lot-grid">
              <div
                v-for="field in lotFields"
                :key="field.key"
                class="po-report-pdf-lot-cell"
                :class="{ 'po-report-pdf-lot-cell--empty': field.key === 'spacer' }"
              >
                <template v-if="field.key !== 'spacer'">
                  <div class="po-report-pdf-lot-cell__label">{{ field.label }}</div>
                  <div class="po-report-pdf-lot-cell__value">{{ field.value }}</div>
                </template>
              </div>
            </div>
          </div>
        </section>

        <section class="po-report-pdf-section">
          <h3 class="po-report-pdf-section-title">{{ photosTitle }}</h3>
          <div class="po-report-pdf-photos">
            <template v-for="(photo, index) in photoSlots">
              <img
                v-if="photo"
                :key="`po-report-photo-${index}`"
                :src="photo"
                alt=""
                class="po-report-pdf-photo"
              />
              <div
                v-else
                :key="`po-report-photo-ph-${index}`"
                class="po-report-pdf-photo po-report-pdf-photo--placeholder"
                aria-hidden="true"
              ></div>
            </template>
          </div>
        </section>

        <section class="po-report-pdf-section po-report-pdf-section--shaded po-report-pdf-section--qr">
          <h3 class="po-report-pdf-section-title">{{ qrCodeTitle }}</h3>
          <div class="po-report-pdf-qr-row">
            <div class="po-report-pdf-qr-block">
              <div class="po-report-pdf-qr-box">
                <qrcode
                  v-if="activeReport.internalQrText"
                  :text="activeReport.internalQrText"
                      :size="112"
                    ></qrcode>
              </div>
              <p class="po-report-pdf-qr-caption mb-0">
                <span class="po-report-pdf-qr-caption__text">
                  {{ internalIdLabel }}:
                  {{ activeReport.internalId || "—" }}
                </span>
              </p>
            </div>
            <div class="po-report-pdf-qr-block">
              <div class="po-report-pdf-qr-box">
                <qrcode
                  v-if="activeReport.externalQrText"
                  :text="activeReport.externalQrText"
                      :size="112"
                    ></qrcode>
              </div>
              <p class="po-report-pdf-qr-caption mb-0">
                <span class="po-report-pdf-qr-caption__text">
                  {{ externalIdLabel }}:
                  {{ activeReport.externalId || "—" }}
                </span>
              </p>
            </div>
          </div>
        </section>
        </div>

        <div class="po-report-pdf-footer">
          <div class="po-report-pdf-footer-left">Dimitra.io</div>
          <div class="po-report-pdf-footer-right">
            {{ pageLabel(1) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import html2canvas from "html2canvas";
import moment from "moment";

const PDF_PAGE_WIDTH_PX = 800;
const PDF_TOTAL_PAGES = 1;
const PHOTO_SLOT_COUNT = 3;

export default {
  name: "PurchaseOrderReportPdfGenerator",
  data() {
    return {
      logoUrl: "/img/dimitra_logo_text.png",
      activeReport: this.emptyReport(),
    };
  },
  computed: {
    reportTitle() {
      return this.$t("memberDataSection.purchaseOrderDetail.reportTitle");
    },
    lotInformationTitle() {
      return this.$t("memberDataSection.purchaseOrderDetail.lotInformation");
    },
    photosTitle() {
      return this.$t("memberDataSection.purchaseOrderDetail.photos");
    },
    qrCodeTitle() {
      return this.$t("memberDataSection.greenCoffee.qrCode");
    },
    internalIdLabel() {
      return this.$t("memberDataSection.parchmentDelivery.bulkImport.internalId");
    },
    externalIdLabel() {
      return this.$t("memberDataSection.greenCoffee.externalId");
    },
    lotFields() {
      const d = this.activeReport;
      return [
        {
          key: "lotId",
          label: this.$t("memberDataSection.purchaseOrderDetail.lotId"),
          value: d.lotId,
        },
        {
          key: "purchaseDate",
          label: this.$t("memberDataSection.purchaseDate"),
          value: d.purchaseDate,
        },
        {
          key: "farmer",
          label: this.$t("memberDataSection.farmer"),
          value: d.farmer,
        },
        {
          key: "farmZone",
          label: this.$t("memberDataSection.purchaseOrderDetail.farmSlashZone"),
          value: d.farmZone,
        },
        {
          key: "coffeeSpecies",
          label: this.$t("memberDataSection.coffeeSpecies"),
          value: d.coffeeSpecies,
        },
        {
          key: "coffeeVariety",
          label: this.$t("memberDataSection.coffeeVariety"),
          value: d.coffeeVariety,
        },
        {
          key: "coffeeType",
          label: this.$t("memberDataSection.coffeeType"),
          value: d.coffeeType,
        },
        {
          key: "coffeeCherryQty",
          label: this.$t("memberDataSection.coffeeCherryQuantity"),
          value: d.coffeeCherryQty,
        },
        {
          key: "coffeeCherryQlty",
          label: this.$t("memberDataSection.coffeeCherryQuality"),
          value: d.coffeeCherryQlty,
        },
        {
          key: "pricePerUnit",
          label: this.$t("memberDataSection.purchaseOrderDetail.pricePerUnit"),
          value: d.pricePerUnit,
        },
        {
          key: "grandTotal",
          label: this.$t("memberDataSection.pOrder.grandTotal"),
          value: d.grandTotal,
        },
        {
          key: "spacer",
          label: "",
          value: "",
        },
      ];
    },
    photoSlots() {
      const urls = this.activeReport.photoUrls || [];
      return Array.from({ length: PHOTO_SLOT_COUNT }, (_, index) => urls[index] || null);
    },
  },
  methods: {
    emptyReport() {
      return {
        lotId: "—",
        purchaseDate: "—",
        farmer: "—",
        farmZone: "—",
        coffeeSpecies: "—",
        coffeeVariety: "—",
        coffeeType: "—",
        coffeeCherryQty: "—",
        coffeeCherryQlty: "—",
        pricePerUnit: "—",
        grandTotal: "—",
        photoUrls: [],
        internalId: null,
        externalId: null,
        internalQrText: "",
        externalQrText: "",
        orderCode: "",
      };
    },
    pageLabel(pageNumber) {
      return this.$t("memberDataSection.purchaseOrderDetail.reportPageOf", {
        page: pageNumber,
        total: PDF_TOTAL_PAGES,
      });
    },
    async generatePdf(purchaseOrder, listRecord = null) {
      if (!purchaseOrder) {
        throw new Error(this.$t("memberDataSection.purchaseOrderDetail.reportDataMissing"));
      }

      this.activeReport = await this.buildReportData(purchaseOrder, listRecord);
      await this.$nextTick();
      await new Promise((resolve) => setTimeout(resolve, 300));
      await this.createPdf();
    },
    async buildReportData(po, listRecord) {
      const row = listRecord || {};
      const merged = {
        ...row,
        ...po,
        orderCode: po.orderCode || row.orderCode,
        external_id: po.external_id ?? row.external_id ?? po.externalId ?? row.externalId,
        externalId: po.externalId ?? row.externalId ?? po.external_id ?? row.external_id,
      };

      const farmerName = [merged.farmer?.firstName, merged.farmer?.middleName, merged.farmer?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      const varietyName =
        merged.coffeeVariety?.name ||
        merged.coffeeVarietyDirect?.name ||
        (Array.isArray(merged.coffeeVariety)
          ? merged.coffeeVariety.map((v) => v.name).filter(Boolean).join(", ")
          : "") ||
        row.coffeeVarietyDirect?.name ||
        (Array.isArray(row.coffeeVariety)
          ? row.coffeeVariety.map((v) => v.name).join(", ")
          : "");
      const qty =
        merged.coffeeCherryQty != null && merged.coffeeCherryQty !== ""
          ? `${merged.coffeeCherryQty} kg`
          : "—";
      const currency = merged.currency || "USD";
      const perUnit = merged.perKgPrice ?? merged.pricePerUnit;
      const computedTotal =
        merged.grandTotal != null && merged.grandTotal !== ""
          ? merged.grandTotal
          : perUnit != null &&
              merged.coffeeCherryQty != null &&
              merged.coffeeCherryQty !== ""
            ? Number(merged.coffeeCherryQty) * Number(perUnit)
            : null;

      const internalId = this.resolveInternalId(merged, row);
      const externalId = this.resolveExternalId(merged, row);
      const rawPhotos = this.collectPhotoUrls(merged.coffeeCherryPic);
      const photoUrls = await this.convertPhotosToBase64(rawPhotos);

      return {
        orderCode: merged.orderCode || "",
        lotId: merged.orderCode || "—",
        purchaseDate: this.formatPurchaseDate(merged.purchasedAt),
        farmer: farmerName || "—",
        farmZone: merged.userFarms?.farmName || "—",
        coffeeSpecies: merged.coffeeSpecies?.name || "—",
        coffeeVariety: varietyName || "—",
        coffeeType: merged.coffeeType?.name || row.coffeeType?.name || "—",
        coffeeCherryQty: qty,
        coffeeCherryQlty: merged.coffeeCherryQlty || "—",
        pricePerUnit:
          perUnit != null && perUnit !== "" ? `${currency} ${this.formatMoney(perUnit)}` : "—",
        grandTotal:
          computedTotal != null && computedTotal !== ""
            ? `${currency} ${this.formatMoney(computedTotal)}`
            : "—",
        photoUrls,
        internalId,
        externalId,
        internalQrText: internalId || "",
        externalQrText: externalId || "",
      };
    },
    formatPurchaseDate(value) {
      if (!value) return "—";
      const parsed = moment(value, [moment.ISO_8601, "MM/DD/YYYY", "DD-MMM-YYYY"], true);
      if (parsed.isValid()) {
        return parsed.format("DD-MMM-YYYY").toUpperCase();
      }
      return String(value).toUpperCase();
    },
    formatMoney(value) {
      const amount = Number(value);
      if (Number.isNaN(amount)) return String(value);
      return amount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    normalizeId(value) {
      const id = value != null ? String(value).trim() : "";
      return id || null;
    },
    resolveInternalId(po, row) {
      return (
        this.normalizeId(po.internalId) ||
        this.normalizeId(po.internal_id) ||
        this.normalizeId(po.recordId) ||
        this.normalizeId(po.orderCode) ||
        this.normalizeId(row.internalId) ||
        this.normalizeId(row.internal_id) ||
        this.normalizeId(row.recordId) ||
        this.normalizeId(row.orderCode) ||
        this.parseQrField(po.qrCodeDataString, [
          "orderCode",
          "internalId",
          "internal_id",
          "recordId",
        ])
      );
    },
    resolveExternalId(po, row) {
      return (
        this.normalizeId(po.external_id) ||
        this.normalizeId(po.externalId) ||
        this.normalizeId(row.external_id) ||
        this.normalizeId(row.externalId) ||
        this.parseQrField(po.qrCodeDataString, ["external_id", "externalId"])
      );
    },
    parseQrField(qrCodeDataString, fields = []) {
      if (!qrCodeDataString || !fields.length) return null;
      try {
        const parsed = JSON.parse(qrCodeDataString);
        for (let i = 0; i < fields.length; i += 1) {
          const value = this.normalizeId(parsed?.[fields[i]]);
          if (value) return value;
        }
      } catch {
        return null;
      }
      return null;
    },
    collectPhotoUrls(pic) {
      if (!pic) return [];
      const items = Array.isArray(pic) ? pic : [pic];
      return items
        .map((item) => {
          if (!item) return null;
          if (typeof item === "string") return item;
          return item.location || item.file_name || item.s3_key || null;
        })
        .filter(Boolean)
        .slice(0, PHOTO_SLOT_COUNT);
    },
    async convertPhotosToBase64(urls) {
      if (!urls.length) return [];
      return Promise.all(
        urls.map(async (url) => {
          try {
            const response = await fetch(url, { mode: "cors", credentials: "omit" });
            if (!response.ok) return url;
            const blob = await response.blob();
            return await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          } catch {
            return url;
          }
        })
      );
    },
    async createPdf() {
      const template = this.$refs.pdfTemplate;
      if (!template) {
        throw new Error("PDF template not found");
      }

      const originalDisplay = template.style.display;
      const originalVisibility = template.style.visibility;
      const originalPosition = template.style.position;
      const originalLeft = template.style.left;
      const originalWidth = template.style.width;

      template.style.display = "block";
      template.style.visibility = "visible";
      template.style.position = "absolute";
      template.style.left = "-9999px";
      template.style.width = `${PDF_PAGE_WIDTH_PX}px`;
      template.style.zIndex = "-1";

      try {
        await this.waitForPdfImages(template);
        await new Promise((resolve) => setTimeout(resolve, 200));

        const { jsPDF } = await import("jspdf");
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const pages = template.querySelectorAll(".po-report-pdf-page");

        for (let i = 0; i < pages.length; i += 1) {
          if (i > 0) pdf.addPage();
          const page = pages[i];
          const canvas = await html2canvas(page, {
            scale: 2,
            useCORS: true,
            allowTaint: false,
            logging: false,
            backgroundColor: "#ffffff",
            width: page.scrollWidth || PDF_PAGE_WIDTH_PX,
            height: page.scrollHeight || page.offsetHeight,
            windowWidth: PDF_PAGE_WIDTH_PX,
            imageTimeout: 30000,
            removeContainer: true,
          });

          const imgWidth = pageWidth;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          pdf.addImage(
            canvas.toDataURL("image/png"),
            "PNG",
            0,
            0,
            imgWidth,
            Math.min(imgHeight, pageHeight)
          );
        }

        const orderCode = (this.activeReport.orderCode || "report").replace(/[^a-z0-9]/gi, "_");
        pdf.save(`Purchase_Order_Report_${orderCode}.pdf`);
      } finally {
        template.style.display = originalDisplay;
        template.style.visibility = originalVisibility;
        template.style.position = originalPosition;
        template.style.left = originalLeft;
        template.style.width = originalWidth;
        template.style.zIndex = "";
      }
    },
    async waitForPdfImages(template) {
      return new Promise((resolve) => {
        const images = template.querySelectorAll("img");
        if (!images.length) {
          resolve();
          return;
        }

        let loadedCount = 0;
        const totalImages = images.length;
        const timeout = setTimeout(() => resolve(), 10000);

        const checkComplete = () => {
          loadedCount += 1;
          if (loadedCount >= totalImages) {
            clearTimeout(timeout);
            resolve();
          }
        };

        images.forEach((img) => {
          img.style.display = "block";
          img.style.visibility = "visible";
          if (img.complete && img.naturalHeight !== 0) {
            checkComplete();
          } else {
            img.onload = checkComplete;
            img.onerror = checkComplete;
          }
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
$po-report-pad-x: 24px;
$po-report-pad-bottom: 24px;
$po-report-page-width: 800px;

.po-report-pdf-template {
  width: $po-report-page-width;
  margin: 0;
  padding: 0;
  background: #fff;
  font-family: Poppins, Arial, sans-serif;
}

.po-report-pdf-page {
  position: relative;
  width: $po-report-page-width;
  min-height: 1120px;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  background: #fff;
}

/* Full-width header: no page padding; banner touches right edge of 800px canvas */
.po-report-pdf-page-header {
  width: $po-report-page-width;
  margin: 0 0 28px;
  padding: 6px 0 16px;
  box-sizing: border-box;
}

.po-report-pdf-header {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  width: $po-report-page-width;
  column-gap: 40px;
  box-sizing: border-box;
}

.po-report-pdf-logo-wrap {
  padding-left: $po-report-pad-x;
  box-sizing: border-box;
}

.po-report-pdf-logo {
  height: 48px;
  width: auto;
  max-width: 240px;
  object-fit: contain;
  display: block;
}

.po-report-pdf-title-banner {
  width: 100%;
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 28px;
  background: #117443;
  border-radius: 8px 0 0 8px;
  box-sizing: border-box;
}

/* Body content: same 24px horizontal padding as before header was split out */
.po-report-pdf-content {
  padding: 0 $po-report-pad-x 72px;
  box-sizing: border-box;
}

.po-report-pdf-title-banner h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #fff;
  text-align: center;
  white-space: nowrap;
  line-height: 1.2;
}

.po-report-pdf-section {
  margin-bottom: 32px;
}

.po-report-pdf-section--shaded {
  width: calc(100% + #{$po-report-pad-x * 2});
  margin-left: -$po-report-pad-x;
  margin-right: -$po-report-pad-x;
  padding: 20px $po-report-pad-x 24px;
  background: #f5f5f5;
  box-sizing: border-box;
}

.po-report-pdf-section-title {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 700;
  color: #212121;
}

.po-report-pdf-lot-panel {
  background: transparent;
  border-radius: 6px;
  padding: 0;
}

.po-report-pdf-lot-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px 16px;
}

.po-report-pdf-lot-cell__label {
  font-size: 11px;
  font-weight: 500;
  color: #9e9e9e;
  margin-bottom: 6px;
  line-height: 1.25;
}

.po-report-pdf-lot-cell__value {
  font-size: 14px;
  font-weight: 700;
  color: #212121;
  line-height: 1.4;
  word-break: break-word;
}

.po-report-pdf-photos {
  display: flex;
  flex-wrap: nowrap;
  gap: 16px;
}

.po-report-pdf-photo {
  width: 136px;
  height: 136px;
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;
  display: block;
  background: #f5f5f5;
}

.po-report-pdf-photo--placeholder {
  border: 1px solid #e0e0e0;
}

.po-report-pdf-qr-row {
  display: flex;
  flex-wrap: nowrap;
  gap: 32px;
}

.po-report-pdf-qr-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.po-report-pdf-qr-box {
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}

.po-report-pdf-qr-caption {
  text-align: center;
}

.po-report-pdf-qr-caption__text {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: #212121;
  white-space: nowrap;
}

.po-report-pdf-footer {
  position: absolute;
  bottom: $po-report-pad-bottom;
  left: $po-report-pad-x;
  right: $po-report-pad-x;
  display: flex;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid #bdbdbd;
  font-size: 11px;
  color: #212121;
  box-sizing: border-box;
}

.po-report-pdf-footer-left {
  font-weight: 600;
}

.po-report-pdf-footer-right {
  color: #757575;
}
</style>
